/**
 * Client for the `passkeyAuth` Cloud Function, patient-portal side
 * (CU-86ajnjka0 server; CU-86ajzr8r0 client).
 *
 * Orchestrates the two-leg WebAuthn flows against the single multi-mode
 * callable, and nothing else — the ceremony itself is PasskeyProvider's job and
 * the session hand-off belongs to the caller. Kept separate so each layer is
 * independently testable.
 *
 * Server-authoritative values (challenge, rpId, timeout, excludeCredentials)
 * come from the challenge leg and are passed through untouched: the client
 * never invents an RP ID or a challenge.
 *
 * PORTED FROM USACS-MIDEO-Admin-Web-v2's `passkeyClient.ts`. Deliberate deltas:
 *  1. `surface: "patient-portal"` on the register-side calls.
 *  2. `functions` is imported from `src/constants/firebaseModular.ts`, which
 *     initialises a NAMED app (`initializeApp(mainConfig, 'modular')`). ⚠️ A bare
 *     `getFunctions()` would throw `No Firebase App '[DEFAULT]'` at runtime — not
 *     at compile time, so the type checker will not catch a regression here.
 *  3. `getPasskeyStatus` and `revokeAllPasskeys` are NOT wrapped — see below.
 *
 * ⚠️ WHY `surface` IS ON CHALLENGE, REGISTER AND LIST/REVOKE BUT NOT VERIFY. It
 * selects the server's RP context — which rpId the ceremony runs under, and
 * which origin allowlist the attestation is checked against. Verify takes NO
 * surface: the server derives the RP from the stored credential binding
 * (CU-86ajxj9jw). Accepting a client-declared surface at verify would let a
 * credential registered under one RP assert under another's origin allowlist —
 * the cross-surface credential confusion that finding exists to close. Do not
 * "make it consistent" by adding it there.
 *
 * ⚠️ TWO SERVER MODES ARE DELIBERATELY NOT WRAPPED HERE.
 *  - `mode: "status"` (would be `getPasskeyStatus`) — returns a count that is
 *    GLOBAL ACROSS SURFACES and cannot be scoped; `PasskeyStatusPayload` has no
 *    `surface` field and deliberately never gained one. A surface-filtered
 *    `listPasskeys()` IS an accurate per-surface count, which is why the
 *    per-credential registry (CU-86ajz1bhx) closed that gap with the list rather
 *    than a `surface` field on status. Two ways to ask the same question is how
 *    they drift apart.
 *  - `revokePasskeys` with no `credId` (would be `revokeAllPasskeys`) — an
 *    account-wide kill-switch that deletes across ALL RPs including the caller's
 *    mobile credentials, AND signs them out everywhere. A regression from
 *    `revokePasskey(credId)` to that call is INVISIBLE from the screen: the
 *    button still works, still reports success, and silently clears every
 *    credential on the account. Not wrapping it means the portal has no such
 *    function to reach for by mistake.
 *
 * Both exist server-side and both are reachable if a real consumer ever needs
 * them — each is a five-line add, and should arrive WITH that consumer rather
 * than ahead of it. The admin console wraps both and calls neither.
 */

import { httpsCallable } from 'firebase/functions';
import type {
  PasskeyChallengePayload,
  PasskeyChallengeResponse,
  PasskeyRegisterPayload,
  PasskeyRegisterResponse,
  PasskeyVerifyPayload,
  PasskeyVerifyResponse,
  PasskeyListPayload,
  PasskeyListResponse,
  RevokePasskeysPayload,
  RevokePasskeysResponse,
  PasskeyAttestationResponse,
  PasskeyAssertionResponse,
  PasskeySurface,
} from '@usacsmideo/types';
import { functions } from '../../constants/firebaseModular';

const FN_PASSKEY_AUTH = 'passkeyAuth';
const FN_REVOKE_PASSKEYS = 'revokePasskeys';

/**
 * This client's surface. Every register-side call from this repo declares it.
 *
 * ⚠️ It is a DECLARATION, not a security boundary — the browser independently
 * enforces that `rpId` be a registrable-domain suffix of the ceremony origin,
 * so a ceremony run under the wrong RP fails in the user agent before any
 * attestation comes back. Its job is to select the correct RP, not to prove
 * anything.
 *
 * `"patient-portal"` is also the server's default when `surface` is absent (the
 * shipped mobile app sends none), but it is passed EXPLICITLY here: it is
 * self-documenting, and it survives a future change to that default. An
 * unrecognised value is rejected by the server rather than defaulted, so a typo
 * fails loudly.
 */
export const PATIENT_PORTAL_SURFACE: PasskeySurface = 'patient-portal';

/**
 * Request a server-issued, single-use challenge for the given ceremony.
 *
 * `surface` is meaningful for `register` (it picks the RP), and harmless on
 * `authenticate` — verify resolves the RP from the stored binding regardless.
 * Sent on both so there is one code path, not two.
 */
export async function requestChallenge(intent: 'register' | 'authenticate'): Promise<PasskeyChallengeResponse> {
  const fn = httpsCallable<PasskeyChallengePayload, PasskeyChallengeResponse>(functions, FN_PASSKEY_AUTH);
  const { data } = await fn({
    mode: 'challenge',
    intent,
    surface: PATIENT_PORTAL_SURFACE,
  });
  return data;
}

/**
 * Submit an attestation to complete enrollment.
 *
 * `reauthProof` is a freshly-minted ID token proving an EXPLICIT reauth just
 * happened (invariant M-E) — the server rejects a merely time-fresh session.
 * There is deliberately no uid parameter: the server binds the credential to
 * `req.auth.uid` unconditionally (REG-UID).
 */
export async function submitRegistration(params: {
  attestation: PasskeyAttestationResponse;
  reauthProof: string;
  deviceLabel?: string;
}): Promise<PasskeyRegisterResponse> {
  const fn = httpsCallable<PasskeyRegisterPayload, PasskeyRegisterResponse>(functions, FN_PASSKEY_AUTH);
  const { data } = await fn({
    mode: 'register',
    attestation: params.attestation,
    reauthProof: params.reauthProof,
    surface: PATIENT_PORTAL_SURFACE,
    ...(params.deviceLabel ? { deviceLabel: params.deviceLabel } : {}),
  });
  return data;
}

/**
 * Submit an assertion to complete login. Returns ONLY a custom token — the
 * server resolves the uid from the stored credential binding (C1), so nothing
 * identifying is sent or returned. The token carries `{ mfa, aal2 }` claims
 * (H-A) that make the resulting session self-describing as MFA-satisfied.
 *
 * ⚠️ NO `surface` — see the file header.
 */
export async function submitAssertion(assertion: PasskeyAssertionResponse): Promise<PasskeyVerifyResponse> {
  const fn = httpsCallable<PasskeyVerifyPayload, PasskeyVerifyResponse>(functions, FN_PASSKEY_AUTH);
  const { data } = await fn({ mode: 'verify', assertion });
  return data;
}

/**
 * List this surface's credentials for the signed-in patient (CU-86ajz1bhx).
 *
 * Self-only: the server reads `req.auth.uid` and there is no target field.
 * `surface: "patient-portal"` is always sent, so the response contains only
 * credentials registered on this portal — never the caller's mobile passkeys.
 *
 * ⚠️ GROUP AND LABEL BY `summary.surface`, NEVER BY `rpId`. The real RP-id values
 * are private to the server's registry and `@usacsmideo/types` exports none of
 * them, so any client-side rpId→surface mapping would mean hardcoding
 * env-specific domains. The server returns `surface` precisely so callers do not
 * have to (adversarial review F2).
 *
 * ⚠️ `deviceLabel` IS CLIENT-SUPPLIED AND BEST-EFFORT — two devices can both
 * self-label "iPhone". `createdAt` is the reliable discriminator. Never key a
 * revoke off the label.
 *
 * ⚠️ `backupEligible` / `backupState` ARE OPTIONAL, AND ABSENT MEANS "NOT
 * RECORDED" — not "single-device". Credentials enrolled before types 1.52.0
 * carry neither flag. Rendering absence as a definitive claim would tell a
 * patient a synced credential is device-bound, which is the opposite of what the
 * stolen-phone case needs.
 */
export async function listPasskeys(): Promise<PasskeyListResponse> {
  const fn = httpsCallable<PasskeyListPayload, PasskeyListResponse>(functions, FN_PASSKEY_AUTH);
  const { data } = await fn({ mode: 'list', surface: PATIENT_PORTAL_SURFACE });
  return data;
}

/**
 * Revoke exactly ONE credential on this surface (CU-86ajz1bhx).
 *
 * Self-only: the server checks the credential belongs to `req.auth.uid` AND to
 * the declared surface's RP before deleting. Unknown credId, someone else's
 * credId, and a wrong-surface credId all fail identically — that uniformity is
 * deliberate, so this cannot be used to probe what an account holds.
 *
 * ⚠️ DOES NOT SIGN THE CALLER OUT. The patient keeps working in the session they
 * are holding while removing another device — the entire point of the control,
 * and the stolen-phone case depends on it.
 *
 * ⚠️ SEND A STRING OR SEND NOTHING. The server rejects a non-string `credId` with
 * `invalid-argument` having destroyed nothing, while ABSENT means revoke-ALL. A
 * caller that passed e.g. an array from a multi-select would previously have
 * escalated into the kill-switch (adversarial review F1) — the server closed
 * that, and this signature keeps the client honest by typing `credId` as
 * `string`. This repo wraps no revoke-all at all; see the file header.
 */
export async function revokePasskey(credId: string): Promise<RevokePasskeysResponse> {
  const fn = httpsCallable<RevokePasskeysPayload, RevokePasskeysResponse>(functions, FN_REVOKE_PASSKEYS);
  const { data } = await fn({ credId, surface: PATIENT_PORTAL_SURFACE });
  return data;
}
